import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";

const PERMS = [
  ["projects", "Projects"],
  ["customers", "Customers"],
  ["billing", "Billing"],
  ["payroll", "Payroll"],
  ["vendors", "Vendors"],
  ["expenses", "Expenses"],
  ["employees", "Employees"],
  ["hours", "Hours"],
  ["email", "Email"],
  ["files", "Files"],
  ["design", "Design"],
  ["design_share", "Design sharing (see everyone's design jobs)"],
  ["leads", "Leads"],
  ["database", "Database"],
  ["accounting", "Accounting"],
  ["personal_vehicle", "Personal vehicle (+2 hrs/day)"],
];
const ROLES = ["owner", "superadmin", "admin", "manager", "employee", "customer", "new_user"];


/** Per-linked-employee toggle: pay from the actual punch-in instead of the
 * 10-min-before-quarter paid-start window ("15-min delay"). Stored on the
 * employee record (skip_warmup_window). */
const WarmupSkipToggle = ({ u, employees, onToggled }) => {
  const emp = employees.find((e) => e.employee_id === u.linked_employee_id);
  if (!emp) return null;
  return (
    <label className="mt-2 flex items-center gap-2 rounded-md border border-white/10 px-3 py-2 text-sm text-white/80" data-testid={`warmup-skip-row-${u.user_id}`}>
      <Checkbox
        checked={!!emp.skip_warmup_window}
        onCheckedChange={async (v) => {
          try {
            await api.put(`/employees/${emp.employee_id}`, { skip_warmup_window: !!v });
            onToggled(emp.employee_id, !!v);
            toast.success(v ? `${emp.name}: hours count from punch-in — no 15-min delay` : `${emp.name}: 15-min paid-start delay restored`);
          } catch { toast.error("Couldn't update the clock setting"); }
        }}
        data-testid={`warmup-skip-${u.user_id}`}
        className="border-white/30 data-[state=checked]:border-[#1a3fd1] data-[state=checked]:bg-[#1a3fd1]"
      />
      Pay from punch-in (no 15-min start delay)
    </label>
  );
};

const PermissionsPanel = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const removeUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name || u.email}? Their login, sessions and linked email accounts are removed. Company records (hours, messages, imports) stay. This cannot be undone.`)) return;
    setSavingId(u.user_id);
    try {
      await api.delete(`/users/${u.user_id}`);
      setUsers((prev) => prev.filter((x) => x.user_id !== u.user_id));
      toast.success(`${u.name || u.email} deleted`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setSavingId(null);
    }
  };

  const load = useCallback(async () => {
    try {
      const [u, p, e] = await Promise.all([
        api.get("/users"),
        api.get("/projects"),
        api.get("/employees"),
      ]);
      setUsers(u.data);
      setProjects(p.data);
      setEmployees(e.data);
    } catch {
      toast.error("Could not load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (userId, body) => {
    setSavingId(userId);
    try {
      const r = await api.put(`/users/${userId}`, body);
      setUsers((prev) => prev.map((u) => (u.user_id === userId ? r.data : u)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSavingId(null);
    }
  };

  if (loading) return <p className="py-6 text-sm text-white/50">Loading users…</p>;

  return (
    <div className="space-y-4" data-testid="permissions-panel">
      <p className="text-xs text-white/40">
        Assign roles and fine-tune each user's access. Changing a role resets permissions to that role's defaults.
      </p>
      {users.map((u) => {
        // Only the built-in primary superadmin account is locked (recovery hatch).
        // Any additional superadmin users can be re-roled by other superadmins/owners.
        const isPrimarySuper = u.user_id === "user_superadmin_dev";
        const isSuper = isPrimarySuper;
        return (
          <div key={u.user_id} data-testid="user-row" className="rounded-lg border border-white/10 bg-white/[0.03] p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-white">{u.name}</p>
                <p className="truncate font-mono text-[11px] text-white/40">{u.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-wider text-white/40">Role</span>
                <select
                  value={isSuper ? "owner" : u.role}
                  disabled={isSuper || savingId === u.user_id}
                  onChange={(e) => save(u.user_id, { role: e.target.value })}
                  data-testid="user-role-select"
                  className="rounded-md border border-white/15 bg-[#0d0d0d] px-2 py-1.5 text-sm text-white disabled:opacity-50"
                >
                  {isSuper ? <option value="owner">superadmin</option> : ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                {!isSuper && u.user_id !== me?.user_id && (
                  <button
                    type="button"
                    onClick={() => removeUser(u)}
                    disabled={savingId === u.user_id}
                    data-testid={`user-delete-${u.user_id}`}
                    title="Delete this user"
                    className="rounded-md border border-white/15 p-1.5 text-white/40 transition-colors hover:border-red-500/40 hover:text-red-400 disabled:opacity-40"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {!isSuper && u.role !== "employee" && (
              <>
                <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {PERMS.map(([key, label]) => (
                    <label key={key} className="flex min-w-0 items-center gap-2 rounded-md border border-white/10 px-3 py-2 text-sm text-white/80">
                      <Checkbox
                        checked={!!u.permissions[key]}
                        onCheckedChange={(v) => save(u.user_id, { permissions: { ...u.permissions, [key]: !!v } })}
                        data-testid={`perm-${key}`}
                        className="border-white/30 data-[state=checked]:border-[#1a3fd1] data-[state=checked]:bg-[#1a3fd1]"
                      />
                      {label}
                    </label>
                  ))}
                </div>

                {u.role === "customer" && (
                  <div className="mt-3 flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-[#8fa4f0]">Linked project</span>
                    <select
                      value={u.customer_project_id || ""}
                      onChange={(e) => save(u.user_id, { customer_project_id: e.target.value })}
                      data-testid="customer-project-select"
                      className="min-w-0 flex-1 truncate rounded-md border border-white/15 bg-[#0d0d0d] px-2 py-1.5 text-sm text-white"
                    >
                      <option value="">— Select a project —</option>
                      {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                    </select>
                  </div>
                )}

                <div className="mt-3 flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-[#8fa4f0]">Linked employee</span>
                  <select
                    value={u.linked_employee_id || ""}
                    onChange={(e) => save(u.user_id, { linked_employee_id: e.target.value })}
                    data-testid="linked-employee-select"
                    className="min-w-0 flex-1 truncate rounded-md border border-white/15 bg-[#0d0d0d] px-2 py-1.5 text-sm text-white"
                  >
                    <option value="">— Not linked —</option>
                    {employees.map((e) => (
                      <option key={e.employee_id} value={e.employee_id}>
                        {e.name}{e.job_title ? ` · ${e.job_title}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <WarmupSkipToggle u={u} employees={employees} onToggled={(empId, val) => setEmployees((prev) => prev.map((e) => e.employee_id === empId ? { ...e, skip_warmup_window: val } : e))} />

                {u.role === "owner" && (
                  <div className="mt-3 flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-[#8fa4f0]">Owner</span>
                    <select
                      value={u.assigned_owner || ""}
                      onChange={(e) => save(u.user_id, { assigned_owner: e.target.value })}
                      data-testid="assigned-owner-select"
                      className="min-w-0 flex-1 truncate rounded-md border border-white/15 bg-[#0d0d0d] px-2 py-1.5 text-sm text-white"
                    >
                      <option value="">— Not assigned —</option>
                      <option value="colin">Colin</option>
                      <option value="brett">Brett</option>
                    </select>
                  </div>
                )}
              </>
            )}

            {isSuper && (
              <div className="mt-4 flex items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-wider text-[#8fa4f0]">Linked employee</span>
                <select
                  value={u.linked_employee_id || ""}
                  onChange={(e) => save(u.user_id, { linked_employee_id: e.target.value })}
                  data-testid="linked-employee-select"
                  className="min-w-0 flex-1 truncate rounded-md border border-white/15 bg-[#0d0d0d] px-2 py-1.5 text-sm text-white"
                >
                  <option value="">— Not linked —</option>
                  {employees.map((e) => (
                    <option key={e.employee_id} value={e.employee_id}>
                      {e.name}{e.job_title ? ` · ${e.job_title}` : ""}
                    </option>
                  ))}
                </select>
                <WarmupSkipToggle u={u} employees={employees} onToggled={(empId, val) => setEmployees((prev) => prev.map((e) => e.employee_id === empId ? { ...e, skip_warmup_window: val } : e))} />
              </div>
            )}

            {u.role === "employee" && !isSuper && (
              <>
                <p className="mt-4 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-white/40">
                  Employees only see their Home / Stats tab and their own profile — no other tabs.
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-[#8fa4f0]">Linked employee</span>
                  <select
                    value={u.linked_employee_id || ""}
                    onChange={(e) => save(u.user_id, { linked_employee_id: e.target.value })}
                    data-testid="linked-employee-select"
                    className="min-w-0 flex-1 truncate rounded-md border border-white/15 bg-[#0d0d0d] px-2 py-1.5 text-sm text-white"
                  >
                    <option value="">— Not linked —</option>
                    {employees.map((e) => (
                      <option key={e.employee_id} value={e.employee_id}>
                        {e.name}{e.job_title ? ` · ${e.job_title}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <WarmupSkipToggle u={u} employees={employees} onToggled={(empId, val) => setEmployees((prev) => prev.map((e) => e.employee_id === empId ? { ...e, skip_warmup_window: val } : e))} />
              </>
            )}
          </div>
        );
      })}
      {users.length === 0 && <p className="text-sm text-white/40">No users yet.</p>}
    </div>
  );
};

export default PermissionsPanel;
